import { useEffect } from 'react';
import SEO, { breadcrumbSchema, organizationSchema } from '@/components/SEO';
import { Link } from 'react-router-dom';
import { Calendar, Users, Award, Eye, Flame, Shield, Wrench, Linkedin, Heart, Lightbulb, Clock, Star, ArrowRight, CheckCircle2, Target, Mic, Music, Baby, MapPin, Zap, ChevronRight, ChevronDown } from 'lucide-react';
import CTASection from '@/components/CTASection';
import PressSection from '@/components/PressSection';
import CyrilleArtistesCarousel from '@/components/CyrilleArtistesCarousel';
import FounderAwards from '@/components/FounderAwards';

import cyrillePhoto from '@/assets/cyrille-lecourt.webp';
import cyrilleMultiCasquettes from '@/assets/cyrille-multi-casquettes.webp';

const stats = [
  { icon: Calendar, label: 'Années d\'expérience', value: '25+' },
  { icon: Award, label: 'Événements depuis avril 2021', value: '250+' },
  { icon: Users, label: 'Artistes dans notre réseau', value: '100+' },
  { icon: Star, label: 'Note Google (32 avis)', value: '4,9/5' },
];

const parcours = [
  { year: '1997', title: 'Début à l\'E.S. Troyes A.C.', desc: 'Cyrille rejoint le club de football professionnel de Troyes. Il y passera 25 ans à gérer billetterie, marketing, commerce et relation client.' },
  { year: '2018', title: 'Premiers événements privés', desc: 'Fort de son expérience terrain, Cyrille commence à organiser des événements privés pour des proches. Le bouche-à-oreille commence.' },
  { year: '2020', title: 'Création de Cygnature Production', desc: 'L\'agence est officiellement créée avec une ambition claire : proposer du haut de gamme accessible à tous les budgets.' },
  { year: '2023', title: '250+ événements', desc: 'Cygnature Production dépasse les 250 événements organisés. La réputation s\'étend bien au-delà de l\'Aube.' },
  { year: '2024', title: 'Expansion régionale', desc: 'L\'agence étend sa zone d\'intervention à tout le Grand Est, la Bourgogne et l\'Île-de-France.' },
  { year: '2025', title: 'Affirmation de l\'identité CYGNATURE', desc: 'L\'agence affirme une signature plus forte : des événements élégants, humains et maîtrisés, portés par un sens du détail, une vraie écoute et une exigence constante.' },
  { year: '2026', title: 'Nouveau cap de développement', desc: 'Cygnature Production accélère son évolution, élargit son réseau, développe ses prestations et confirme sa présence sur des événements toujours plus ambitieux.' },
  { year: 'Demain', title: 'L\'histoire continue', desc: 'Chaque événement écrit une nouvelle page de l\'aventure CYGNATURE, avec toujours la même ambition : surprendre, rassembler et marquer durablement les esprits.' },
];

const forces = [
  { icon: Eye, title: 'L\'œil bionique à 360°', desc: 'Il voit ce que les autres ne voient pas encore. Il anticipe les problèmes avant qu\'ils n\'existent. Chaque détail est analysé, vérifié, optimisé pour la perfection.' },
  { icon: Flame, title: 'Les 8 bras de la Pieuvre', desc: 'Traiteur, DJ, décoration, logistique, artistes, timing, budget, imprévus — tout est tenu, simultanément, avec une précision chirurgicale.' },
  { icon: Shield, title: 'Le mental du Ninja', desc: 'Calme en surface, redoutablement efficace dans l\'ombre. Aucun couac ne transpire, aucun stress ne vous atteint. Le Ninja veille sur tout.' },
  { icon: Wrench, title: 'L\'ingéniosité du MacGyver', desc: 'Avec peu, il fait beaucoup. Avec beaucoup, il fait l\'extraordinaire. Il s\'adapte à chaque budget sans jamais sacrifier l\'émotion ni la qualité.' },
];




const APropos = () => {
  return (
    <>
      <SEO
        title="À propos | Cygnature Production Troyes"
        description="Découvrez Cyrille Lecourt et Cygnature Production. 25 ans d'expérience, 250+ événements, un parcours unique au service de vos moments les plus précieux. Agence événementielle à Troyes."
        path="/a-propos"
        keywords={['Cyrille Lecourt','agence événementielle Troyes','wedding planner homme','expertise événementielle']}
        jsonLd={[
          breadcrumbSchema([{ name: 'Accueil', path: '/' }, { name: 'À propos', path: '/a-propos' }]),
          organizationSchema(),
          {
            '@context': 'https://schema.org',
            '@type': 'Person',
            name: 'Cyrille Lecourt',
            jobTitle: 'Fondateur — Cygnature Production',
            worksFor: { '@type': 'Organization', name: 'Cygnature Production' },
            url: 'https://cygnature-production.com/a-propos',
            sameAs: ['https://www.linkedin.com/in/cyrille-lecourt-5425381bb/'],
          },
        ]}
      />
      {/* Hero */}
      <section className="px-6 lg:px-8 py-20 md:py-28 bg-petrol text-petrol-foreground">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-heading font-semibold uppercase tracking-[0.15em] text-primary mb-4">À propos</p>
          <h1 className="font-heading text-3xl md:text-5xl font-bold leading-tight">
            Cyrille Lecourt & Cygnature Production
          </h1>
          <p className="mt-5 text-petrol-foreground/60 max-w-2xl text-sm md:text-base leading-relaxed">
            L'agence événementielle incarne une vision exigeante, humaine et élégante de l'événementiel. Agence haut de gamme, créateur d'expériences sur-mesure, nous imaginons des événements pensés avec précision, vécus avec émotion et portés par le sens du détail.
          </p>
          <p className="mt-3 text-petrol-foreground/60 max-w-2xl text-sm md:text-base leading-relaxed">
            Derrière Cygnature, un homme au parcours singulier, guidé par l'écoute, l'empathie et l'envie sincère de créer des instants forts, justes et mémorables. Cette sensibilité, alliée à une vraie maîtrise du terrain, donne à chaque projet une dimension unique.
          </p>
          <p className="mt-3 text-petrol-foreground/60 max-w-2xl text-sm md:text-base leading-relaxed">
            Basés à Troyes, dans l'Aube, nous intervenons dans tout le Grand Est et partout en France pour concevoir, coordonner et sublimer vos événements privés, professionnels et artistiques.
          </p>
          <p className="mt-3 text-primary font-heading italic text-sm">
            « Suivez vos Rêves, ils connaissent le chemin »
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="px-6 lg:px-8 py-12 md:py-16 bg-background">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <p className="text-xs font-heading font-semibold uppercase tracking-[0.15em] text-primary mb-2">La confiance</p>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground">L'excellence en chiffres</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
          {stats.map((s) => (
            <div key={s.label} className="text-center p-4 md:p-6 rounded-xl border border-border bg-card">
              <s.icon size={18} className="text-primary mx-auto mb-2" strokeWidth={1.5} />
              <p className="font-heading text-xl md:text-2xl font-bold text-foreground">{s.value}</p>
              <p className="text-[10px] md:text-xs text-muted-foreground mt-0.5">{s.label}</p>
            </div>
          ))}
          </div>
        </div>

      </section>



      {/* Parcours */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-card">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <div>
            <p className="text-xs font-heading font-semibold uppercase tracking-[0.15em] text-primary mb-2">Le parcours</p>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground">Un homme forgé par la vie</h2>
            <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
              Tout commence à 3 ans. Cyrille Lecourt n'est pas un homme ordinaire. Son histoire commence bien avant les projecteurs et les salles des fêtes — elle commence dans un couloir d'hôpital. Un cancer rarissime, la solitude, la perte d'un œil. Là où la vie aurait pu briser un enfant, elle a construit un combattant.
            </p>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
              Avant de créer Cygnature Production, Cyrille a passé plus de 25 ans au sein de l'E.S. Troyes A.C. — billetterie, marketing, commerce, relation client, gestion des supporters… Il a tout vécu, tout géré, tout optimisé. Ce n'est pas un entrepreneur qui découvre le métier. C'est un professionnel aguerri, créatif, rigoureux, charismatique et orienté résultat.
            </p>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
              Sa devise ? <em>« La maladie, la vie avec le handicap, m'a forgé un moral de CHAMPION ! »</em> Ce moral, il le met au service de chaque client, de chaque événement. Avec la même intensité, la même générosité, la même envie de créer des moments inoubliables.
            </p>
            <blockquote className="mt-4 pl-4 border-l-2 border-primary italic text-sm text-foreground/70">
              "La maladie, la vie avec le handicap, m'a forgé un moral de CHAMPION !"
            </blockquote>
          </div>
          <div>
            <p className="text-xs font-heading font-semibold uppercase tracking-[0.15em] text-primary mb-2">Les casquettes</p>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground">Multi casquettes, le TOUT en UN</h2>
            <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
              Wedding Planner, Officiant de Cérémonie Laïque, Wedding Designer, Agent et Producteur artistique — Cyrille est le tout en un. Et il fait partie des <strong className="text-foreground">5% d'hommes Wedding Planners en France</strong>. Une rareté. Une fierté. Un regard différent qui apporte une complémentarité précieuse.
            </p>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
              Sa philosophie est profondément humaine : <em>« L'humain est un axe important dans ma façon d'agir. Je prends place à côté de mes clients, je suis leur double, j'agis comme si c'est pour moi — avec mon cœur et mon expérience du terrain. »</em>
            </p>
            <ul className="mt-4 space-y-2">
              {[
                'Organisation d\'événements — Galas, séminaires, festivals, anniversaires, team building',
                'Production de talents — Plus de 100 artistes pluridisciplinaires',
                'Wedding Planner & Designer — Du rêve à la réalité, votre mariage haut de gamme',
                'Officiant de cérémonies laïques — Des moments d\'une intensité rare',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
                  <span className="text-sm text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Portrait 4 costumes */}
      <div className="px-6 lg:px-8 -mt-6 pb-14 md:pb-20 bg-card">
        <div className="max-w-2xl mx-auto">
          <div className="p-3 bg-background rounded-sm shadow-md border border-border/60" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.10), inset 0 0 0 1px rgba(255,255,255,0.04)' }}>
            <div className="border border-border/30">
              <img src={cyrilleMultiCasquettes} alt="Cyrille Lecourt avec ses multiples casquettes" className="w-full h-auto object-cover" loading="lazy" />
            </div>
          </div>
          <p className="text-center text-sm italic text-muted-foreground mt-4">
            Multicasquettes — une seule signature.
          </p>
        </div>
      </div>

      {/* Carrousel Cyrille et ses artistes */}
      <CyrilleArtistesCarousel />

      {/* Timeline — enhanced with gradient line and decorated dots */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-background">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-heading font-semibold uppercase tracking-[0.15em] text-primary mb-2">Chronologie</p>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground">Notre histoire en quelques dates</h2>
          </div>
          {/* Frise chronologique */}
          <div className="relative">
            {/* Desktop : parcours en serpentin (1→4 puis 5→8 en sens inverse) */}
            <div className="hidden lg:block">
              {[parcours.slice(0, 4), parcours.slice(4, 8).reverse()].map((row, rowIdx) => {
                const isReversed = rowIdx === 1;
                return (
                  <div key={rowIdx} className="relative">
                    {/* Ligne horizontale dorée pleine */}
                    <div className="absolute top-6 left-[12.5%] right-[12.5%] h-0.5 bg-primary/30" aria-hidden="true" />

                    {/* Flèches de progression entre pastilles */}
                    <div className="absolute top-6 left-0 right-0 grid grid-cols-4 -translate-y-1/2 pointer-events-none" aria-hidden="true">
                      {row.map((_, i) => (
                        <div key={i} className="flex justify-end pr-0">
                          {i < row.length - 1 && (
                            <ChevronRight
                              size={20}
                              className="text-primary"
                              strokeWidth={2.5}
                              style={{ transform: `translateX(-50%) ${isReversed ? 'rotate(180deg)' : ''}` }}
                            />
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Connecteur vertical entre rangée 1 et 2 (descend à droite) */}
                    {rowIdx === 0 && (
                      <div className="absolute top-6 right-[12.5%] w-0.5 h-[calc(100%+3.5rem)] bg-primary/30" aria-hidden="true">
                        <ChevronDown size={20} className="text-primary absolute -bottom-2 left-1/2 -translate-x-1/2" strokeWidth={2.5} />
                      </div>
                    )}

                    <div className="grid grid-cols-4 gap-6 items-stretch">
                      {row.map((p) => (
                        <div key={p.year} className="relative flex flex-col items-center text-center h-full">
                          {/* Pastille année — plus large pour accueillir 4 caractères */}
                          <div className="relative z-10 min-w-12 h-12 px-3 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-heading font-bold text-sm shadow-lg ring-[6px] ring-background shrink-0 whitespace-nowrap">
                            {p.year}
                          </div>
                          <div className="h-3 shrink-0" aria-hidden="true" />
                          <div className="rounded-lg border border-border bg-card p-4 shadow-sm w-full flex-1">
                            <h3 className="font-heading text-sm font-bold text-foreground mb-1.5">{p.title}</h3>
                            <p className="text-xs text-muted-foreground leading-relaxed">{p.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>

                    {rowIdx === 0 && <div className="h-14" aria-hidden="true" />}
                  </div>
                );
              })}
            </div>

            {/* Mobile / tablette : frise verticale avec flèches */}
            <div className="lg:hidden relative">
              <div className="absolute top-2 bottom-2 left-5 w-0.5 bg-primary/30" aria-hidden="true" />
              <ol className="space-y-5">
                {parcours.map((p, i) => (
                  <li key={p.year} className="relative pl-20">
                    <div className="absolute left-0 top-0 z-10 min-w-11 h-11 px-3 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-heading font-bold text-xs shadow-md ring-[5px] ring-background whitespace-nowrap">
                      {p.year}
                    </div>
                    <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
                      <h3 className="font-heading text-base font-bold text-foreground mb-1.5">{p.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{p.desc}</p>
                    </div>
                    {i < parcours.length - 1 && (
                      <ChevronDown size={18} className="text-primary absolute left-5 -translate-x-1/2 -bottom-4 z-10" strokeWidth={2.5} aria-hidden="true" />
                    )}
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* Ses forces — gradient accent cards */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-card">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-heading font-semibold uppercase tracking-[0.15em] text-primary mb-2">Les super-pouvoirs</p>
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground">Ses forces — multiples et redoutables</h2>
          <p className="mt-3 text-sm text-muted-foreground leading-relaxed max-w-2xl">Les clients le surnomment <strong className="text-foreground">le Ninja de l'événementiel</strong> ou encore <strong className="text-foreground">la Pieuvre — celle aux 8 bras et ses 1 000 ventouses</strong>. Voici pourquoi :</p>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {forces.map((f, i) => (
              <div key={f.title} className="relative p-6 rounded-xl border border-border bg-background overflow-hidden group hover:border-primary/20 hover:shadow-md transition-all">
                {/* Top gradient bar */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-primary/60 to-accent" />
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 border border-primary/10 flex items-center justify-center mb-4">
                  <f.icon size={22} className="text-primary" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading font-bold text-sm text-foreground">{f.title}</h3>
                <p className="mt-2 text-xs text-muted-foreground leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>




      {/* Philosophie */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-card">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <div>
            <p className="text-xs font-heading font-semibold uppercase tracking-[0.15em] text-primary mb-2">Philosophie</p>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground">Une philosophie profondément humaine</h2>
            <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
              « L'humain est un axe important dans ma façon d'agir et d'interagir. J'axe toutes mes relations sur le rapport humain. Je prends place à côté de mes clients, je suis leur double, j'agis comme si c'est pour moi — avec mon cœur et mon expérience du terrain. »
            </p>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
              Un homme qui a traversé la maladie, l'isolement, le handicap, et en a tiré une force rare : celle de tout donner pour les autres. Sa récompense ? Le sourire et la joie de ses clients. Chaque événement réussi est une victoire personnelle, un moment où tout prend sens.
            </p>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
              Cyrille ne travaille pas POUR ses clients. Il travaille AVEC eux. Cette nuance fait toute la différence dans l'approche, dans l'écoute, dans la qualité du résultat final.
            </p>
          </div>
          <div>
            <p className="text-xs font-heading font-semibold uppercase tracking-[0.15em] text-primary mb-2">Vision</p>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground">Notre vision de l'événementiel</h2>
            <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
              L'événementiel n'est pas un métier. C'est un art. L'art de créer des moments qui marquent les esprits et les cœurs. Chez Cygnature, nous croyons que chaque instant compte, que chaque détail fait la différence, que chaque événement est une opportunité de créer de la magie.
            </p>
            <blockquote className="mt-4 pl-4 border-l-2 border-primary italic text-sm text-foreground/70">
              "Je suis le couteau Suisse de l'événement, « l'Éponge » qui absorbe le négatif pour ne laisser paraître que le positif."
            </blockquote>
            <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
              Vous lui confiez un rêve. Il vous rend un souvenir inoubliable. <strong className="text-foreground">On s'occupe de TOUT. No stress. Juste votre moment.</strong>
            </p>
            <div className="mt-6 p-5 rounded-xl border border-border bg-background">
              <h3 className="font-heading font-bold text-sm text-foreground mb-3">Ce que nos clients disent :</h3>
              <ul className="space-y-2">
                {[
                  '"Vous pouvez y aller les yeux fermés" — Aldo F.',
                  '"Un homme avec un cœur en or" — Cécile B.',
                  '"Le Ninja de l\'événementiel" — Sabine H.',
                ].map((q) => (
                  <li key={q} className="text-xs text-muted-foreground italic flex items-start gap-2">
                    <Star size={12} className="text-primary shrink-0 mt-0.5 fill-primary" />
                    {q}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Équipe */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-background">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs font-heading font-semibold uppercase tracking-[0.15em] text-primary mb-2">Le fondateur</p>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground">Le visage derrière Cygnature</h2>
          </div>
          <div className="flex flex-col items-center">
            {/* Portrait principal */}
            <div className="w-48 h-48 md:w-56 md:h-56 rounded-full overflow-hidden border-4 border-primary/20 mx-auto">
              <img src={cyrillePhoto} alt="Cyrille Lecourt — Fondateur de Cygnature Production" className="w-full h-full object-cover object-top" />
            </div>
            <h3 className="font-heading font-bold text-base text-foreground mt-4">Cyrille Lecourt</h3>
            <p className="text-sm text-muted-foreground mt-1">Fondateur & Dirigeant</p>
            <p className="text-xs text-muted-foreground mt-2 leading-relaxed max-w-sm text-center">
              Créatif, rigoureux, charismatique. Coordination globale, direction artistique, sélection des prestataires et supervision de l'expérience client. 25 ans d'expérience, plus de 250 événements, l'un des 5% d'hommes Wedding Planners en France. Le Ninja de l'événementiel, la Pieuvre aux 8 bras.
            </p>
            <a href="https://www.linkedin.com/in/cyrille-lecourt-5425381bb/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 mt-3 text-xs text-primary hover:text-primary/80 transition-colors">
              <Linkedin size={14} strokeWidth={1.5} /> Profil LinkedIn
            </a>
            <FounderAwards align="center" className="max-w-md" />
          </div>
        </div>
      </section>

      <CTASection
        title="Envie de nous confier votre projet ?"
        subtitle="Premier échange gratuit et sans engagement. Nous créerons ensemble l'événement de vos rêves. Devis sous 48 à 72h."
      />
    </>
  );
};

export default APropos;
